/**
 * 操作履歴画面
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHistories, getHistory } from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Eye } from 'lucide-react';

export default function Histories() {
  const [page, setPage] = useState(1);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<any>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'histories', page],
    queryFn: () => getHistories({ page, limit: 20 }),
  });

  const handleView = async (history: any) => {
    try {
      const fullHistory = await getHistory(history.id);
      setSelectedHistory(fullHistory.history);
      setIsViewDialogOpen(true);
    } catch (error) {
      console.error('履歴情報の取得に失敗しました:', error);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-gray-900">操作履歴</h1>
          <p className="text-base text-gray-500 mt-2">操作履歴の管理</p>
        </div>

        {/* 履歴一覧 */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">履歴一覧</div>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">エラーが発生しました</div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-gray-200 hover:bg-transparent">
                    <TableHead className="text-base font-bold text-gray-700">ID</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">ユーザー</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">アクションタイプ</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">説明</TableHead>
                    <TableHead className="text-base font-bold text-gray-700">作成日時</TableHead>
                    <TableHead className="text-base font-bold text-gray-700 text-right">操作</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.histories?.map((history: any) => (
                    <TableRow key={history.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{history.id}</TableCell>
                      <TableCell className="py-4">
                        <div>
                          <div className="text-base font-medium text-gray-900">{history.user_name || '-'}</div>
                          <div className="text-base text-gray-500">{history.user_email || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <span className="inline-flex items-center justify-center rounded-full border border-blue-300 bg-blue-50 px-3 py-1 text-base font-bold text-blue-700">
                          {history.action_type || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="py-4 max-w-xs truncate text-base text-gray-700">
                        {history.description || '-'}
                      </TableCell>
                      <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">
                        {new Date(history.created).toLocaleString('ja-JP')}
                      </TableCell>
                      <TableCell className="py-4">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleView(history)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                            aria-label="詳細"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* ページネーション */}
              {data?.pagination && (
                <div className="flex justify-between items-center mt-5 pt-4">
                  <div className="text-base text-gray-700">
                    全 {data.pagination.total} 件中 {((page - 1) * 20 + 1)} - {Math.min(page * 20, data.pagination.total)} 件を表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                      className="h-9 rounded-lg border-gray-200"
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* 詳細表示ダイアログ */}
        <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>履歴詳細</DialogTitle>
              <DialogDescription>履歴の詳細情報</DialogDescription>
            </DialogHeader>
            {selectedHistory && (
              <div className="space-y-4">
                <div>
                  <strong>ユーザー:</strong> {selectedHistory.user_name || '-'} ({selectedHistory.user_email || '-'})
                </div>
                <div>
                  <strong>アクションタイプ:</strong> {selectedHistory.action_type || '-'}
                </div>
                <div>
                  <strong>説明:</strong> {selectedHistory.description || '-'}
                </div>
                {selectedHistory.action_details && (
                  <div>
                    <strong>アクション詳細:</strong>
                    <pre className="text-xs bg-gray-100 p-4 rounded overflow-auto mt-2">
                      {JSON.stringify(selectedHistory.action_details, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}

